<template>
  <div class="static-sitemap-page container py-4">
    <!-- Global Loader Overlay -->
    <div
      v-if="loading.global"
      class="loading-overlay d-flex justify-content-center align-items-center"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between mb-3">
      <h2 class="mb-0 text-primary">Static Sitemap Pages</h2>
      <button
        class="btn btn-outline-primary btn-sm"
        @click="fetchStaticPages"
        :disabled="loading.global"
      >
        <span
          v-if="loading.global"
          class="spinner-border spinner-border-sm mr-1"
        ></span>
        Atualizar
      </button>
    </div>

    <!-- Add Card -->
    <div class="card shadow-sm mb-4" :class="{ 'opacity-50': loading.add }">
      <div class="card-body position-relative py-3">
        <div v-if="loading.add" class="section-loader"></div>
        <h5 class="mb-1">Adicionar página</h5>
        <p class="text-secondary mb-3">
          Cria uma entrada na tabela <code>sitemap_pages</code>.
        </p>

        <div class="form-row">
          <div class="form-group col-md-2 mb-2">
            <label class="col-form-label">Site</label>
            <select
              v-model="form.site"
              class="form-control form-control-sm"
              :disabled="loading.add"
            >
              <option value="idivia.com">idivia.com</option>
              <option value="idivia.eu">idivia.eu</option>
            </select>
          </div>

          <div class="form-group col-md-2 mb-2">
            <label class="col-form-label">Language</label>
            <select
              v-model="form.language"
              class="form-control form-control-sm"
              :disabled="loading.add || form.site === 'idivia.eu'"
            >
              <option v-if="form.site === 'idivia.eu'" value="">
                AUTO (via sitemap_info)
              </option>
              <option value="EN">EN</option>
              <option value="PT">PT</option>
              <option value="ES">ES</option>
              <option value="JA">JA</option>
            </select>
            <small v-if="form.site === 'idivia.eu'" class="text-muted">
              Para <b>idivia.eu</b> a língua vem do <code>sitemap_info</code>.
            </small>
          </div>

          <div class="form-group col-md-3 mb-2">
            <label class="col-form-label">Page</label>
            <input
              type="text"
              v-model="form.page"
              class="form-control form-control-sm"
              placeholder="home / about / contact"
              :disabled="loading.add"
            />
          </div>

          <div class="form-group col-md-5 mb-2">
            <label class="col-form-label">URL (.com) / Handle path (.eu)</label>
            <div class="d-flex">
              <input
                type="text"
                v-model="form.url"
                class="form-control form-control-sm mr-2"
                placeholder="url (para .com) ex: https://idivia.com/about/"
                :disabled="loading.add || form.site === 'idivia.eu'"
              />
              <input
                type="text"
                v-model="form.handle"
                class="form-control form-control-sm"
                placeholder="handle (para .eu) ex: pages/about ou url/lan/dbinfo"
                :disabled="loading.add || form.site === 'idivia.com'"
              />
            </div>
            <small class="text-muted">
              Regra: idivia.com usa <b>url</b> | idivia.eu usa <b>handle</b>
            </small>
          </div>
        </div>

        <button
          class="btn btn-primary btn-sm"
          @click="createStatic"
          :disabled="loading.add"
        >
          <span
            v-if="loading.add"
            class="spinner-border spinner-border-sm mr-1"
          ></span>
          Adicionar
        </button>
      </div>
    </div>

    <!-- List -->
    <div class="card shadow-sm" :class="{ 'opacity-50': loading.list }">
      <div class="card-body position-relative py-3">
        <div v-if="loading.list" class="section-loader"></div>

        <div class="d-flex align-items-center justify-content-between mb-2">
          <h5 class="mb-0">Lista</h5>
          <small class="text-muted">{{ pages.length }} registos</small>
        </div>

        <div v-if="!pages.length && !loading.list" class="text-muted">
          Sem páginas estáticas.
        </div>

        <div v-else class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead class="thead-light">
              <tr>
                <th style="min-width: 120px">Site</th>
                <th style="min-width: 110px">Language</th>
                <th style="min-width: 140px">Page</th>
                <th>URL</th>
                <th>Handle</th>
                <th style="min-width: 160px" class="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="p in pages"
                :key="`${p.site}-${p.page}-${p.language || 'null'}`"
              >
                <td>
                  <span class="badge badge-pill badge-primary">{{
                    p.site
                  }}</span>
                </td>
                <td>
                  <span class="badge badge-pill badge-secondary">
                    {{ p.language || 'AUTO' }}
                  </span>
                </td>
                <td class="font-weight-bold">{{ p.page }}</td>
                <td>
                  <small class="text-monospace">{{ p.url || '-' }}</small>
                </td>
                <td>
                  <small class="text-monospace">{{ p.handle || '-' }}</small>
                </td>
                <td class="text-right">
                  <button
                    class="btn btn-sm btn-outline-success mr-2"
                    @click="startEdit(p)"
                    :disabled="loading.edit || loading.delete"
                  >
                    Editar
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="deleteStatic(p)"
                    :disabled="loading.delete || loading.edit"
                  >
                    Apagar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit Modal (Bootstrap-ish simple) -->
    <div v-if="edit.open" class="modal-backdrop-custom">
      <div class="modal-custom card shadow">
        <div class="card-body position-relative">
          <div v-if="loading.edit" class="section-loader"></div>

          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">Editar</h5>
            <button class="btn btn-sm btn-outline-secondary" @click="closeEdit">
              X
            </button>
          </div>

          <div class="form-row">
            <div class="form-group col-md-3 mb-2">
              <label class="col-form-label">Site</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="edit.data.site"
                disabled
              />
            </div>

            <div class="form-group col-md-3 mb-2">
              <label class="col-form-label">Language</label>
              <input
                type="text"
                class="form-control form-control-sm"
                :value="
                  edit.data.site === 'idivia.eu' ? 'AUTO' : edit.data.language
                "
                disabled
              />
            </div>

            <div class="form-group col-md-6 mb-2">
              <label class="col-form-label">Page</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="edit.data.page"
                disabled
              />
            </div>

            <div class="form-group col-md-12 mb-2">
              <label class="col-form-label">URL / Handle</label>
              <div class="d-flex">
                <input
                  type="text"
                  class="form-control form-control-sm mr-2"
                  v-model="edit.data.url"
                  :disabled="loading.edit || edit.data.site === 'idivia.eu'"
                  placeholder="url (para .com)"
                />
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="edit.data.handle"
                  :disabled="loading.edit || edit.data.site === 'idivia.com'"
                  placeholder="handle (para .eu)"
                />
              </div>
              <small class="text-muted">
                Regra: idivia.com usa <b>url</b> | idivia.eu usa <b>handle</b>
              </small>
            </div>
          </div>

          <div class="d-flex justify-content-end">
            <button
              class="btn btn-sm btn-outline-secondary mr-2"
              @click="closeEdit"
            >
              Cancelar
            </button>
            <button
              class="btn btn-sm btn-primary"
              @click="saveEdit"
              :disabled="loading.edit"
            >
              <span
                v-if="loading.edit"
                class="spinner-border spinner-border-sm mr-1"
              ></span>
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StaticSitemapPage',
  data() {
    return {
      pages: [],
      form: {
        site: 'idivia.com',
        page: '',
        language: 'EN',
        url: '',
        handle: '',
      },
      edit: {
        open: false,
        data: { site: '', page: '', language: '', url: '', handle: '' },
      },
      loading: {
        global: false,
        list: false,
        add: false,
        edit: false,
        delete: false,
      },
    };
  },
  methods: {
    async confirmAdd(message) {
      const result = await this.$swal.fire({
        title: 'Adicionar?',
        text: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return result.isConfirmed;
    },
    async confirmUpdate(message) {
      const result = await this.$swal.fire({
        title: 'Atualizar?',
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return result.isConfirmed;
    },
    async confirmDelete(message) {
      const result = await this.$swal.fire({
        title: 'Apagar?',
        text: message,
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
      });
      return result.isConfirmed;
    },

    normalizePayload({ site, page, language, url, handle }) {
      const cleanSite = (site || '').trim();
      const payload = {
        site: cleanSite,
        page: (page || '').trim(),
        language: cleanSite === 'idivia.eu' ? null : (language || '').trim(),
        url: url && String(url).trim() ? String(url).trim() : null,
        handle: handle && String(handle).trim() ? String(handle).trim() : null,
      };

      if (payload.site === 'idivia.com') payload.handle = null;
      if (payload.site === 'idivia.eu') payload.url = null;

      return payload;
    },

    async fetchStaticPages() {
      this.loading.global = true;
      this.loading.list = true;

      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}siteMap/static`
        );
        this.pages = data.data || [];
      } catch (error) {
        console.error('Error fetching static sitemap pages:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao carregar páginas',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }

      this.loading.list = false;
      this.loading.global = false;
    },

    async createStatic() {
      const payload = this.normalizePayload(this.form);

      if (!payload.site || !payload.page) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Campos em falta',
          text: 'site e page são obrigatórios.',
        });
        return;
      }

      if (payload.site === 'idivia.com' && !payload.language) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Campos em falta',
          text: 'language é obrigatório para idivia.com.',
        });
        return;
      }

      if (!payload.url && !payload.handle) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Campos em falta',
          text: 'Precisas de url (.com) ou handle (.eu).',
        });
        return;
      }

      const langLabel =
        payload.site === 'idivia.eu' ? 'AUTO' : payload.language;

      const ok = await this.confirmAdd(
        `Adicionar ${payload.site} | ${payload.page} | ${langLabel}?`
      );

      if (!ok) return;

      this.loading.add = true;
      try {
        await axios.post(`${this.$store.state.apiUrl}siteMap/static`, payload);

        // reset form
        this.form.page = '';
        this.form.url = '';
        this.form.handle = '';

        await this.fetchStaticPages();

        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Criado',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error creating static sitemap page:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao criar',
          text: 'Verifica se já existe (PK) e tenta novamente.',
        });
      }
      this.loading.add = false;
    },

    startEdit(p) {
      this.edit.open = true;
      this.edit.data = {
        site: p.site,
        page: p.page,
        language: p.language || '', // .eu vem null
        url: p.url || '',
        handle: p.handle || '',
      };
    },

    closeEdit() {
      if (this.loading.edit) return;
      this.edit.open = false;
      this.edit.data = {
        site: '',
        page: '',
        language: '',
        url: '',
        handle: '',
      };
    },

    async saveEdit() {
      const payload = this.normalizePayload(this.edit.data);

      if (!payload.url && !payload.handle) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Campos em falta',
          text: 'Precisas de url (.com) ou handle (.eu).',
        });
        return;
      }

      const langLabel =
        payload.site === 'idivia.eu' ? 'AUTO' : payload.language;

      const ok = await this.confirmUpdate(
        `Guardar ${payload.site} | ${payload.page} | ${langLabel}?`
      );
      if (!ok) return;

      this.loading.edit = true;
      try {
        await axios.patch(`${this.$store.state.apiUrl}siteMap/static`, payload);
        await this.fetchStaticPages();
        this.closeEdit();

        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Atualizado',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error updating static sitemap page:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao atualizar',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
      this.loading.edit = false;
    },

    async deleteStatic(p) {
      const langLabel = p.site === 'idivia.eu' ? 'AUTO' : p.language;
      const ok = await this.confirmDelete(
        `Eliminar ${p.site} | ${p.page} | ${langLabel}?`
      );

      if (!ok) return;

      this.loading.delete = true;
      try {
        await axios.delete(`${this.$store.state.apiUrl}siteMap/static`, {
          data: {
            site: p.site,
            page: p.page,
            language: p.site === 'idivia.eu' ? null : p.language,
          },
        });

        await this.fetchStaticPages();

        this.$swal.fire({
          toast: true,
          icon: 'success',
          title: 'Apagado',
          timer: 2000,
          position: 'top-end',
        });
      } catch (error) {
        console.error('Error deleting static siteMap page:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Erro ao apagar',
          text: 'Por favor, tente novamente mais tarde.',
        });
      }
      this.loading.delete = false;
    },
  },
  created() {
    this.fetchStaticPages();
  },
  watch: {
    'form.site'(v) {
      if (v === 'idivia.com') {
        this.form.handle = '';
        if (!this.form.language) this.form.language = 'EN';
      }
      if (v === 'idivia.eu') {
        this.form.url = '';
        this.form.language = ''; // AUTO
      }
    },
  },
};
</script>

<style scoped>
.static-sitemap-page {
  font-family: 'Poppins', sans-serif;
  position: relative;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  z-index: 10;
}
.section-loader {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.6);
  z-index: 5;
}
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}
.modal-custom {
  width: 900px;
  max-width: 95vw;
  border-radius: 8px;
}
.opacity-50 {
  opacity: 0.5;
  pointer-events: none;
}
</style>
